import React, { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Container, Typography, CircularProgress, Button } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import Cookies from "js-cookie";
import MessageScreen from "../postExamUpload/MessageScreen";

const QR_IMAGE_SIZE = 200;

const buildQrImageUrl = (targetUrl) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${QR_IMAGE_SIZE}x${QR_IMAGE_SIZE}&data=${encodeURIComponent(
    targetUrl
  )}`;

export default function PostExamUploadQr() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isSafeBrowser = window.hubbleAPI?.isKiosk?.() ?? false;

  const token =
    searchParams.get("token") || [...searchParams.keys()][0] || null;
  const project = Cookies.get("test_engine_project");

  const targetUrl = useMemo(() => {
    if (!token || !project) return null;
    return `${window.location.origin}/${project}/post-exam-upload?${token}`;
  }, [token, project]);

  const handleContinueOnThisDevice = () => {
    navigate(`/${project}/post-exam-upload?${token}`);
  };

  if (!targetUrl) {
    return (
      <MessageScreen
        title="Page Not Found"
        description="The upload link is invalid or no longer exists."
        showHomeButton={false}
      />
    );
  }

  if (imgError) {
    return (
      <MessageScreen
        title="Something Went Wrong"
        description="Unable to generate the QR code. Please try again."
      />
    );
  }

  const qrCard = (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #E5E7EB",
        borderRadius: "16px",
        backgroundColor: "#F8F9FC",
        p: { xs: 3, sm: 4 },
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: "#0F172A", mb: 1 }}>
        Upload Files Using Your Phone
      </Typography>
      <Typography sx={{ fontSize: "14px", color: "#64748B", lineHeight: 1.6, mb: 3 }}>
        Scan the QR code below with your mobile phone. You will be
        redirected to the secure file upload page.
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: QR_IMAGE_SIZE + 24,
          height: QR_IMAGE_SIZE + 24,
          mx: "auto",
          mb: 2,
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          backgroundColor: "#fff",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        {!imgLoaded && <CircularProgress size={26} sx={{ color: "#3F41D1" }} />}
        <Box
          component="img"
          src={buildQrImageUrl(targetUrl)}
          alt="QR code to upload answer sheets"
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
          sx={{
            width: QR_IMAGE_SIZE,
            height: QR_IMAGE_SIZE,
            display: imgLoaded ? "block" : "none",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
        <QrCode2Icon sx={{ fontSize: 16, color: "#94A3B8" }} />
        <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
          Scan this QR code using your phone camera
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100dvh",
        overflowY: "auto",
        backgroundColor: "#ffffff",
        px: 2,
        py: { xs: 3, sm: 4 },
      }}
    >
      <Container maxWidth="md">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#DCFCE7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <CheckIcon sx={{ color: "#16A34A", fontSize: 34 }} />
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "24px", sm: "30px" },
              fontWeight: "bold",
              color: "#0F172A",
              mb: 1.5,
            }}
          >
            Exam Submitted Successfully
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              color: "#64748B",
              maxWidth: 520,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Your exam has been submitted successfully. Please upload the
            required files to complete your examination process.
          </Typography>
        </Box>

        {/* ── Scan-with-phone card ──────────────────────────────────────── */}
        {isSafeBrowser ? (
          <Box sx={{ maxWidth: 420, mx: "auto" }}>{qrCard}</Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            {qrCard}

            {/* Right: continue on laptop */}
            <Box
              sx={{
                flex: 1,
                border: "1px solid #E5E7EB",
                borderRadius: "16px",
                backgroundColor: "#ffffff",
                p: { xs: 3, sm: 4 },
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#E8F0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2.5,
                }}
              >
                <LaptopMacIcon sx={{ color: "#2563EB", fontSize: 30 }} />
              </Box>

              <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: "#0F172A", mb: 1 }}>
                Continue on This Laptop
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#64748B", lineHeight: 1.6, mb: 3, maxWidth: 320 }}>
                You can upload the required files directly from this computer
                without using your phone.
              </Typography>

              <Button
                onClick={handleContinueOnThisDevice}
                sx={{
                  border: "1.5px solid #2563EB",
                  color: "#2563EB",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "rgba(37,99,235,0.06)" },
                }}
              >
                Continue Upload Using Laptop
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
